import { useQuery } from 'data-fetching-client';
import { useCallback, useMemo } from 'react';
import { GET_PRODUCT_INVENTORY_QUERY } from '../../../../__generated__/chirp/com/hubspot/buying/rpc/ProductRpc.dfc.new';
import { skuCategoryNameToBundleTypeOrProductLine } from '../../constants';
import { logApiError, logApiSuccess } from '../../utils/apiLogging';
import { isValidProductLevel, isValidSkuCategoryName } from '../../utils/typeguards';
import { useProductDetails } from '../product/useProductDetails';
import { useIsInAppAuth } from '../user/useUserInfo';
import { compareProductLevels, useHighestProductLevelFromInventory } from './useHighestProductLevel';
export const useProductInventory = ({
  skip = false
} = {}) => {
  const isInApp = useIsInAppAuth();
  const version = 'v2';
  const handleCompleted = useCallback(() => {
    logApiSuccess({
      queryName: 'GET_PRODUCT_INVENTORY_QUERY',
      operation: 'getProductInventory',
      version
    });
  }, [version]);
  const handleError = useCallback(error => {
    logApiError(error, {
      queryName: 'GET_PRODUCT_INVENTORY_QUERY',
      operation: 'getProductInventory',
      version
    });
  }, [version]);
  const {
    data,
    loading,
    error
  } = useQuery(GET_PRODUCT_INVENTORY_QUERY, {
    onCompleted: handleCompleted,
    onError: handleError,
    skip: skip || !isInApp
  });
  const productInventory = data === null || data === void 0 ? void 0 : data.result;
  const products = productInventory === null || productInventory === void 0 ? void 0 : productInventory.products;
  const crmSuiteLevel = productInventory === null || productInventory === void 0 ? void 0 : productInventory.crmSuiteLevel;
  const highestProductLevelOwned = productInventory === null || productInventory === void 0 ? void 0 : productInventory.highestProductLevelOwned;
  return {
    products,
    crmSuiteLevel,
    highestProductLevelOwned,
    loading,
    error
  };
};
export const useProductFromProductInventoryByApiName = ({
  apiName,
  skip = false
}) => {
  const {
    products,
    loading,
    error
  } = useProductInventory({
    skip: !apiName || skip
  });
  const matchingProduct = useMemo(() => {
    return products === null || products === void 0 ? void 0 : products.find(product => product.productApiName === apiName);
  }, [products, apiName]);
  return {
    product: matchingProduct,
    loading,
    error
  };
};
export const useProductFromProductInventoryBySkuId = ({
  skuId
}) => {
  const {
    products,
    loading,
    error
  } = useProductInventory({
    skip: !skuId
  });
  const matchingProduct = useMemo(() => {
    return products === null || products === void 0 ? void 0 : products.find(product => product.skuId === skuId);
  }, [products, skuId]);
  const matchingQuantityPack = useMemo(() => {
    if (!matchingProduct && products) {
      for (const product of products) {
        var _product$quantityPack;
        const quantityPack = (_product$quantityPack = product.quantityPackDetails) === null || _product$quantityPack === void 0 ? void 0 : _product$quantityPack.find(qp => qp.skuId === skuId);
        if (quantityPack) {
          return quantityPack;
        }
      }
    }
    return undefined;
  }, [products, skuId, matchingProduct]);
  return {
    product: matchingProduct || matchingQuantityPack,
    loading,
    error
  };
};
export const useIsFreePortal = () => {
  const {
    products,
    loading,
    error
  } = useProductInventory();
  return {
    isFreePortal: !(products !== null && products !== void 0 && products.length),
    loading,
    error
  };
};
export const useIsPaidPortal = () => {
  const {
    products,
    loading,
    error
  } = useProductInventory();
  return {
    isPaidPortal: !!(products !== null && products !== void 0 && products.length),
    loading,
    error
  };
};

/**
 * Hook to get the CRM Suite ownership level for the current portal.
 * Replaces the Redux selector `crmSuiteOwnershipLevel`.
 */
export const useCrmSuiteOwnershipLevel = () => {
  const {
    crmSuiteLevel,
    loading,
    error
  } = useProductInventory();
  return {
    crmSuiteLevel: crmSuiteLevel !== null && crmSuiteLevel !== void 0 ? crmSuiteLevel : null,
    loading,
    error
  };
};

/**
 * @description
 * Returns the owned bundle product and bundle type that includes the specified product.
 * Replaces getBundleTypeIfInProductInventory and getOwnedBundleProductContainingHub.
 *
 * @returns {
 *  bundleProduct: Product | undefined,
 *  bundleType: BundleType | undefined,
 *  skuCategoryName: SkuCategoryName | undefined,
 *  loading: boolean,
 *  error: ApolloError | undefined,
 * }
 */
export const useOwnedBundleForProduct = ({
  apiName
}) => {
  const {
    products: ownedProducts,
    loading: inventoryLoading,
    error
  } = useProductInventory();
  const bundleProduct = useMemo(() => {
    if (!inventoryLoading && ownedProducts && apiName) {
      return ownedProducts.find(product => {
        if (!product.bundle || !product.bundledProducts) return false;
        return product.bundledProducts.some(bundledProduct => bundledProduct.productApiName === apiName);
      });
    }
  }, [ownedProducts, apiName, inventoryLoading]);
  const {
    productDetails,
    loading: detailsLoading
  } = useProductDetails({
    productApiName: bundleProduct === null || bundleProduct === void 0 ? void 0 : bundleProduct.productApiName
  });
  const skuCategoryName = useMemo(() => {
    if (bundleProduct && 'skuCategoryName' in bundleProduct) {
      return bundleProduct.skuCategoryName;
    }
    if (bundleProduct && productDetails && !detailsLoading) {
      return productDetails.skuCategoryName;
    }
  }, [bundleProduct, productDetails, detailsLoading]);
  const bundleType = skuCategoryName && skuCategoryNameToBundleTypeOrProductLine[skuCategoryName];
  return {
    bundleProduct,
    bundleType,
    skuCategoryName,
    ownsProductViaBundle: !!bundleProduct,
    loading: inventoryLoading || detailsLoading,
    error
  };
};

/**
 * Hook to determine if a given product level would be a downgrade from the current highest owned product level
 * within the same SKU category.
 *
 * @param productLevel - The product level to compare against current ownership
 * @param skuCategoryName - The SKU category to check within (e.g., MARKETING_PRODUCTS, SALES_PRODUCTS)
 * @returns {object} Object containing isDowngrade boolean, loading state, and error state
 */
export const useIsDowngrade = ({
  productLevel,
  skuCategoryName
}) => {
  const {
    highestProductLevel: highestProductLevelOwnedForSkuCategory,
    loading,
    error
  } = useHighestProductLevelFromInventory({
    skuCategoryName
  });

  // Early return for invalid or undefined inputs
  if (!isValidProductLevel(productLevel) || !isValidSkuCategoryName(skuCategoryName)) {
    return {
      isDowngrade: false,
      loading,
      error
    };
  }
  if (!highestProductLevelOwnedForSkuCategory) {
    return {
      isDowngrade: false,
      loading,
      error
    };
  }

  // If the levels are the same, the source will be the first input in the compareProductLevels call (in this case, productLevel)
  // So if the source is productLevel, then we know the product is the same or higher than the highest owned product in the SKU category
  // Therefore, it is not a downgrade. Otherwise, it is a downgrade.
  const {
    source
  } = compareProductLevels([{
    level: productLevel,
    source: 'productLevel'
  }, {
    level: highestProductLevelOwnedForSkuCategory,
    source: 'highestProductLevelOwnedForSkuCategory'
  }]);
  return {
    isDowngrade: source !== 'productLevel',
    loading,
    error
  };
};
export const useOwnedProductApiNames = () => {
  const {
    products,
    loading,
    error
  } = useProductInventory();
  const ownedProductApiNames = useMemo(() => {
    var _products$map;
    return (_products$map = products === null || products === void 0 ? void 0 : products.map(product => product.productApiName)) !== null && _products$map !== void 0 ? _products$map : [];
  }, [products]);
  return {
    ownedProductApiNames,
    loading,
    error
  };
};